import * as React from 'react';
import { cn } from '../../lib/cn';
import type { PolymorphicComponentProps } from '../../lib/types';

/**
 * Maximum width of the page column.
 *
 * - `sm`: a single narrow form or choice list (32rem)
 * - `md`: a reading column, a settings page or a page-level error (42rem)
 * - `fluid`: a single focused tool, such as patient search: full width below lg, half the viewport on lg+, kept between 40rem and 50rem
 * - `lg`: an admin page with tables (64rem)
 * - `xl`: a multi-column workspace, such as the patient journal (97.5rem)
 */
type PageContainerWidth = 'sm' | 'md' | 'fluid' | 'lg' | 'xl';

/**
 * Padding inside the page column.
 *
 * - `page`: the standard page gutter
 * - `page-flush`: the page gutter on mobile, no side padding on lg+ where the column no longer touches the viewport edge
 * - `none`: the children own their padding
 */
type PageContainerPadding = 'none' | 'page' | 'page-flush';

const widthClasses = {
  sm: 'max-w-lg',
  md: 'max-w-2xl',
  fluid: 'max-w-200 lg:w-1/2 lg:min-w-160',
  lg: 'max-w-5xl',
  xl: 'max-w-390',
} satisfies Record<PageContainerWidth, string>;

const paddingClasses = {
  none: '',
  page: 'px-4 py-8 lg:px-6',
  'page-flush': 'px-4 py-8 lg:px-0',
} satisfies Record<PageContainerPadding, string>;

interface PageContainerOwnProps {
  /** Maximum width of the column. @default 'md' */
  width?: PageContainerWidth;
  /** Padding inside the column. @default 'page' */
  padding?: PageContainerPadding;
  /** Additional layout classes. */
  className?: string;
}

/**
 * Full props for the PageContainer component.
 */
export type PageContainerProps<T extends React.ElementType = 'div'> = PolymorphicComponentProps<
  T,
  PageContainerOwnProps
>;

/**
 * PageContainer centres a page column, caps it at a named width and applies the page gutter.
 */
export function PageContainer<T extends React.ElementType = 'div'>({
  as,
  width = 'md',
  padding = 'page',
  className,
  ref,
  ...props
}: PageContainerProps<T>) {
  const Component = as ?? 'div';

  return (
    <Component
      ref={ref}
      className={cn('mx-auto w-full', widthClasses[width], paddingClasses[padding], className)}
      {...props}
    />
  );
}
